import { useState } from "react";
import { useParams, Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  archiveChore,
  completeChore,
  createChore,
  listChores,
  type Chore,
  type ChoreCadence,
} from "../lib/api-modules/allowance.js";
import { listFamilyMembers } from "../lib/api-modules/family-members.js";
import { Card, Pill, SectionHeader } from "../components/design-system/index.js";
import { Toast, useToast } from "../components/toast.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { formatCentsCompact } from "../lib/util/money.js";

/**
 * Phase 13 — `/family/<member_id>/allowance` page.
 *
 * Two modes:
 *   - **Kid-facing** (member.age >= 5): big checkbox grid of chores;
 *     tapping a chore records a `chore_completion` ledger entry.
 *   - **Parent scaffolding** (member.age < 5): chore editor only;
 *     "Buckets activate when {name} turns 5" placeholder.
 *
 * Age computed from `date_of_birth` at render. Kids without a DOB
 * default to parent-scaffolding mode (no kid-facing UI without a known
 * birthday).
 */
const KID_FACING_MIN_AGE = 5;

export function FamilyMemberAllowancePage() {
  const { memberId } = useParams<{ memberId: string }>();
  const toast = useToast();

  const familyQ = useQuery({
    queryKey: ["family-members"],
    queryFn: () => listFamilyMembers(),
  });
  const choresQ = useQuery({
    queryKey: ["allowance", "chores", memberId ?? "(all)"],
    queryFn: () => listChores(memberId),
    enabled: !!memberId,
  });

  if (!memberId) {
    return <div className="p-6">Missing member_id</div>;
  }
  const member = familyQ.data?.family_members.find(
    (m) => m.member_id === memberId,
  );
  const age =
    member?.date_of_birth !== undefined
      ? computeYears(member.date_of_birth)
      : null;
  const kidFacing = age !== null && age >= KID_FACING_MIN_AGE;

  return (
    <div className="mx-auto max-w-4xl p-6 lg:p-8">
      <SectionHeader
        title={
          <>
            Allowance · {member?.name ?? "…"}{" "}
            {age !== null && (
              <span className="text-sm font-normal text-[var(--color-muted)]">
                ({age} yrs old)
              </span>
            )}
          </>
        }
        sub={
          kidFacing
            ? "Tap a chore when you finish it. The money goes into your buckets — Spend, Save, and Give."
            : age === null
              ? "Set this member's date of birth to activate their kid-facing allowance UI."
              : `Buckets activate when ${member?.name ?? "this member"} turns ${KID_FACING_MIN_AGE}. Until then, scaffold their chores here.`
        }
        action={
          <Link
            to={`/family/${memberId}/buckets`}
            className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-sm hover:bg-[var(--color-elevated)]"
          >
            View buckets →
          </Link>
        }
      />

      {choresQ.isLoading && <p className="text-sm">Loading chores…</p>}
      {choresQ.isError && (
        <p className="text-sm text-[var(--color-danger)]">
          {choresQ.error instanceof Error ? choresQ.error.message : "Failed"}
        </p>
      )}
      {choresQ.data && (
        <>
          {kidFacing ? (
            <KidChoresGrid
              memberId={memberId}
              chores={choresQ.data.chores}
              onComplete={() =>
                toast.show("Chore completed! Buckets updated.")
              }
            />
          ) : (
            <p className="mt-3 mb-6 rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] p-3 text-sm text-[var(--color-muted)]">
              Kid-facing checkbox UI hidden until age {KID_FACING_MIN_AGE}.
              You can edit chores below at any age.
            </p>
          )}
          <ChoreEditor memberId={memberId} chores={choresQ.data.chores} />
        </>
      )}

      <Toast message={toast.message} />
    </div>
  );
}

function KidChoresGrid({
  memberId,
  chores,
  onComplete,
}: {
  memberId: string;
  chores: readonly Chore[];
  onComplete: () => void;
}) {
  const queryClient = useQueryClient();
  const active = chores.filter((c) => !c.is_archived);
  const mut = useMutation({
    mutationFn: (choreId: string) =>
      completeChore(memberId, { chore_id: choreId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["allowance", "ledger", memberId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["allowance", "buckets", memberId],
        }),
      ]);
      onComplete();
    },
  });

  if (active.length === 0) {
    return (
      <Card className="mt-3 mb-6">
        <p className="text-sm text-[var(--color-muted)]">
          No chores yet — ask a parent to add one below!
        </p>
      </Card>
    );
  }

  return (
    <div className="mt-3 mb-6 grid gap-3 sm:grid-cols-2">
      {active.map((c) => (
        <button
          key={c.chore_id}
          type="button"
          disabled={mut.isPending}
          onClick={() => mut.mutate(c.chore_id)}
          className="flex flex-col items-start gap-2 rounded-lg border-2 border-[var(--color-border-default)] bg-[var(--color-card)] p-4 text-left transition hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)]/5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="flex w-full items-center justify-between">
            <span className="font-display text-base font-medium text-[var(--color-text)]">
              {c.name}
            </span>
            <Pill tone="accent">{formatCentsCompact(c.value_cents)}</Pill>
          </div>
          {c.description && (
            <span className="text-xs text-[var(--color-muted)]">
              {c.description}
            </span>
          )}
          <span className="text-xs text-[var(--color-muted)]">
            Spend {c.bucket_split.spend_pct}% · Save {c.bucket_split.save_pct}%
            · Give {c.bucket_split.give_pct}%
          </span>
        </button>
      ))}
    </div>
  );
}

function ChoreEditor({
  memberId,
  chores,
}: {
  memberId: string;
  chores: readonly Chore[];
}) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState(""); // dollars as text
  const [newCadence, setNewCadence] = useState<ChoreCadence>("weekly");
  const [splitSpend, setSplitSpend] = useState(60);
  const [splitSave, setSplitSave] = useState(30);
  const [splitGive, setSplitGive] = useState(10);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => {
      const valueDollars = Number(newValue);
      if (Number.isNaN(valueDollars) || valueDollars <= 0) {
        throw new Error("Value must be a positive number");
      }
      if (splitSpend + splitSave + splitGive !== 100) {
        throw new Error("Bucket split must sum to 100");
      }
      return createChore({
        member_id: memberId,
        name: newName.trim(),
        value_cents: Math.round(valueDollars * 100),
        bucket_split: {
          spend_pct: splitSpend,
          save_pct: splitSave,
          give_pct: splitGive,
        },
        cadence: newCadence,
      });
    },
    onSuccess: async () => {
      setIsAdding(false);
      setNewName("");
      setNewValue("");
      setError(null);
      await queryClient.invalidateQueries({
        queryKey: ["allowance", "chores"],
      });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });
  const archiveMut = useMutation({
    mutationFn: (choreId: string) => archiveChore(choreId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["allowance", "chores"],
      });
    },
  });

  return (
    <Card className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-medium">Chores</h3>
        {!isAdding && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-sm hover:bg-[var(--color-elevated)]"
          >
            + Add chore
          </button>
        )}
      </div>
      {isAdding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate();
          }}
          className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-[var(--color-border-default)] p-3 sm:grid-cols-2"
        >
          <div>
            <label className="mb-1 block text-xs font-medium">Name</label>
            <input
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className={inputClass}
              placeholder="Make bed"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Value (USD)
            </label>
            <input
              required
              type="number"
              step="0.25"
              min="0"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className={inputClass}
              placeholder="1.00"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Cadence</label>
            <select
              value={newCadence}
              onChange={(e) => setNewCadence(e.target.value as ChoreCadence)}
              className={inputClass}
            >
              <option value="one_time">One time</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="sm:col-span-2 grid grid-cols-3 gap-2">
            <SplitInput
              label="Spend"
              value={splitSpend}
              onChange={setSplitSpend}
            />
            <SplitInput
              label="Save"
              value={splitSave}
              onChange={setSplitSave}
            />
            <SplitInput
              label="Give"
              value={splitGive}
              onChange={setSplitGive}
            />
          </div>
          {error && (
            <p className="sm:col-span-2 text-xs text-[var(--color-danger)]">
              {error}
            </p>
          )}
          <div className="sm:col-span-2 flex gap-2">
            <button
              type="submit"
              disabled={createMut.isPending}
              className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            >
              {createMut.isPending ? "Saving…" : "Add chore"}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setError(null);
              }}
              className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <ul className="divide-y divide-[var(--color-border-default)]">
        {chores
          .filter((c) => !c.is_archived)
          .map((c) => (
            <li
              key={c.chore_id}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[var(--color-text)]">{c.name}</p>
                <p className="text-xs text-[var(--color-muted)]">
                  {formatCentsCompact(c.value_cents)} · {c.cadence} ·{" "}
                  {c.bucket_split.spend_pct}/{c.bucket_split.save_pct}/
                  {c.bucket_split.give_pct}
                </p>
              </div>
              <ConfirmButton
                label="Archive"
                ariaLabel={`Archive ${c.name}`}
                onConfirm={() => archiveMut.mutate(c.chore_id)}
                isPending={
                  archiveMut.isPending &&
                  archiveMut.variables === c.chore_id
                }
                size="xs"
              />
            </li>
          ))}
      </ul>
    </Card>
  );
}

function SplitInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium">{label} %</span>
      <input
        type="number"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={inputClass}
      />
    </label>
  );
}

const inputClass =
  "w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-2 py-1.5 text-sm text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]";

function computeYears(dob: string): number {
  const parts = dob.split("-").map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const ref = new Date();
  let age = ref.getUTCFullYear() - y;
  const refMonth = ref.getUTCMonth() + 1;
  const refDay = ref.getUTCDate();
  if (refMonth < m || (refMonth === m && refDay < d)) age -= 1;
  return age;
}
